import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import FormField, { Input } from '../components/FormField';

export default function Gallery() {
  const { isAdmin } = useAuth();
  const [albums, setAlbums] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/api/gallery/albums').then((r) => setAlbums(r.data)).catch(() => setAlbums([]));
  useEffect(() => { load(); }, []);

  const createAlbum = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.post('/api/gallery/albums', { name });
      setName('');
      setCreating(false);
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler beim Erstellen'); }
    finally { setSaving(false); }
  };

  const deleteAlbum = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Album inkl. aller Fotos wirklich löschen?')) return;
    try { await api.delete(`/api/gallery/albums/${id}`); load(); } catch { /* ignore */ }
  };

  if (albums === null) {
    return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="page-title mb-0">📸 Galerie</h1>
          <p className="text-sm text-slate-500 mt-1">Eure schönsten Momente von der LAN, sortiert nach Events.</p>
        </div>
        {isAdmin && <Button onClick={() => setCreating((v) => !v)}>{creating ? 'Abbrechen' : '+ Album erstellen'}</Button>}
      </div>

      {creating && (
        <div className="card border border-ocean-200 mb-6">
          <h3 className="section-title">Neues Album</h3>
          {error && <div className="alert-error mb-4">{error}</div>}
          <form onSubmit={createAlbum} className="flex flex-wrap items-end gap-3">
            <FormField label="Name" required className="mb-0 flex-1 min-w-[180px]">
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="z.B. LAN Frühjahr 2026" />
            </FormField>
            <Button type="submit" loading={saving}>Erstellen</Button>
          </form>
        </div>
      )}

      {albums.length === 0 ? (
        <div className="card text-center py-14">
          <p className="text-slate-400 text-sm">Noch keine Alben angelegt.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {albums.map((a) => (
            <Link
              to={`/galerie/${a.id}`}
              key={a.id}
              className="card p-0 overflow-hidden hover:shadow-card-hover transition-shadow duration-200 no-underline text-inherit flex flex-col"
            >
              <div className="aspect-square bg-slate-100 relative">
                {a.cover_photo_url ? (
                  <img src={a.cover_photo_url} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300">📷</div>
                )}
                {isAdmin && (
                  <button
                    onClick={(e) => deleteAlbum(e, a.id)}
                    className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-0 cursor-pointer"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="p-3">
                <p className="font-bold text-ocean-900 text-sm leading-tight truncate">{a.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{a.photo_count} Foto{a.photo_count !== 1 ? 's' : ''}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
